/**
 * The external store every part of the kiosk keeps its state in: one value, one owner that writes it,
 * any number of readers. React reads it through `useStore` with a selector, so a reader re-renders only
 * when the slice it selected changes (a streaming chat never re-renders the shell).
 */

export type Listener = () => void;

export interface Store<S> {
  readonly name: string;
  get(): S;
  set(next: S | ((prev: S) => S)): void;
  subscribe(listener: Listener): () => void;
}

export function createStore<S>(name: string, initial: S): Store<S> {
  let state = initial;
  const listeners = new Set<Listener>();
  const store: Store<S> = {
    name,
    get: () => state,
    set(next) {
      const value = typeof next === 'function' ? (next as (prev: S) => S)(state) : next;
      if (Object.is(value, state)) return;
      state = value;
      for (const l of [...listeners]) l();
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
  storeRegistry.add(store as Store<unknown>);
  return store;
}

/** Every store ever created, for the dev inspector. Weak references are not needed: stores live as long as the app. */
export const storeRegistry = new Set<Store<unknown>>();

/** Shallow equality for selectors that return a fresh object or array of stable members. */
export function shallowEqual<T>(a: T, b: T): boolean {
  if (Object.is(a, b)) return true;
  if (typeof a !== 'object' || typeof b !== 'object' || a === null || b === null) return false;
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  const ka = Object.keys(a);
  const kb = Object.keys(b);
  if (ka.length !== kb.length) return false;
  return ka.every((k) => Object.is((a as Record<string, unknown>)[k], (b as Record<string, unknown>)[k]));
}
